import * as React from "react";
import { RadioGroup as BaseRadioGroup } from "react-ui";
import { css } from "@emotion/react";

import { Tooltip } from "../../Internals/Tooltip/Tooltip";
import { FocusManagementObserver } from "../../Services/FocusManagement/FocusManagementObserver";
import { useTestPageObject } from "../../Services/TestPageObjects/PageObjectExposure";
import { emptyRef } from "../../TypingUtils";
import { useMetrics } from "../../Internals/Metrics/MetricsProviderContext";
import { VALUE_EDITED } from "../../metricsAction";
import { AbsoluteModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { ControlTooltipProps } from "../../Internals/Tooltip/ControlTooltipProps";
import { ControlValidationProps } from "../../Validations/ValidationProps";
import { Text } from "../../ValueViewers/Text/Text";

import { RadioGroupTestPageObject } from "./RadioGroupTestPageObject";

export interface RadioGroupProps extends ControlTooltipProps, ControlValidationProps {
    value: undefined | string;
    onChange: (value: string) => void;
    bindingPath: AbsoluteModelPath;
    children: React.ReactNode;
    disabled?: boolean;
    items: Array<[string, React.ReactNode]>;
    isReadOnly?: boolean;
    isLink?: boolean;
    onClick?: () => void;
    "data-tid"?: string;
}

export function RadioGroup(props: RadioGroupProps): JSX.Element {
    const innerRadioGroup = React.useRef<BaseRadioGroup<string>>(emptyRef);
    const tooltipValue = props.error || props.warning;
    const tooltipTrigger = tooltipValue != undefined && tooltipValue !== "" ? "hover&focus" : "closed";
    const tooltipPosition = props.tooltipPosition ?? "right middle";
    const track = useMetrics();

    const handleChange = (value: string) => {
        track(VALUE_EDITED, { path: props.bindingPath.toLegacyPath(), newValue: value, oldValue: props.value });
        props.onChange(value);
    };

    const handleRequestFocus = (): void => {
        innerRadioGroup.current?.focus();
    };

    useTestPageObject(() => RadioGroupTestPageObject(props.value, handleChange), [props.value]);

    let radioGroupValue = "";
    const [_, radioGroupSelectedItem] = props.items.find(item => item.includes(props.value)) ?? [];
    if (radioGroupSelectedItem != undefined) {
        if (typeof radioGroupSelectedItem === "string" || typeof radioGroupSelectedItem === "number") {
            radioGroupValue = radioGroupSelectedItem?.toString();
        } else if (typeof radioGroupSelectedItem !== "boolean" && "props" in radioGroupSelectedItem) {
            if (
                "children" in radioGroupSelectedItem.props &&
                typeof radioGroupSelectedItem.props.children === "object"
            ) {
                radioGroupValue = radioGroupSelectedItem.props.children;
            } else {
                radioGroupValue = radioGroupSelectedItem.props.children.toString();
            }
        }
    }

    return props.isReadOnly ? (
        typeof radioGroupValue === "object" ? (
            <div>{radioGroupValue}</div>
        ) : (
            <Text
                value={radioGroupValue}
                data-tid={props["data-tid"]}
                bindingPath={props.bindingPath}
                onClick={props.onClick}
                isLink={props.isLink}
            />
        )
    ) : (
        <FocusManagementObserver onRequestFocus={handleRequestFocus}>
            <Tooltip
                useWrapper={false}
                trigger={tooltipTrigger}
                value={tooltipValue}
                width={props.tooltipWidth}
                pos={tooltipPosition}>
                <BaseRadioGroup<string>
                    css={baselineWorkaround}
                    data-tid={props["data-tid"]}
                    ref={innerRadioGroup}
                    disabled={props.disabled}
                    items={props.items}
                    value={props.value}
                    error={Boolean(props.error)}
                    warning={Boolean(props.warning)}
                    onValueChange={handleChange}>
                    {props.children}
                </BaseRadioGroup>
            </Tooltip>
        </FocusManagementObserver>
    );
}

const baselineWorkaround = css({
    display: "inline-flex",
    flexDirection: "column",
});
